'use client';

/**
 * @file 홈/랜딩 페이지
 * @description 미인증 사용자에게 가치 제안과 데모를 보여주고, 인증된 사용자는 /dashboard로 보냄.
 *              friend-scale 베타지만 표준 SaaS 랜딩 패턴(Hero / 3-step / Features / CTA repeat).
 */

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import {
  Music,
  Sparkles,
  Wand2,
  Upload,
  ArrowRight,
  Check,
  Zap,
  Eye,
  Laptop,
} from 'lucide-react';

import { AppButton, AppCard, Footer } from '@/components/common';
import { SocialProofSection } from '@/components/landing/SocialProofSection';
import {
  VideoLivePreview,
  type VisualizerType,
} from '@/components/playlists/VideoLivePreview';
import { getAccessToken } from '@/lib/auth/cookies';

// 데모 자동 순환 데이터
const COLOR_CYCLE = ['#7C3AED', '#2563EB', '#10B981', '#F59E0B', '#EF4444'];
const VIZ_CYCLE: VisualizerType[] = ['spectrum', 'particle', 'character'];
const TRACK_TITLES = [
  '오후 4시의 카페',
  '비 오는 날의 재즈',
  '운동 부스트 — 80분',
  '깊은 집중 BGM',
  '여름밤 드라이브',
];

export default function HomePage() {
  const router = useRouter();

  // 인증 상태 확인 — 로그인된 사용자는 dashboard로 즉시 이동
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {return;}
    const token = getAccessToken();
    if (token) {
      router.replace('/dashboard');
    } else {
      setAuthChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 라이브 데모 — 4초마다 옵션 자동 변경 (데스크톱 시각적 어트랙션)
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!authChecked) {return undefined;}
    const t = setInterval(() => setTick((n) => n + 1), 4000);
    return () => clearInterval(t);
  }, [authChecked]);

  if (!authChecked) {
    // 인증 체크 동안 빈 화면 — 깜빡임 방지
    return null;
  }

  const color = COLOR_CYCLE[tick % COLOR_CYCLE.length];
  const visualizerType = VIZ_CYCLE[tick % VIZ_CYCLE.length];
  const trackTitle = TRACK_TITLES[tick % TRACK_TITLES.length];
  const posY = 50 + ((tick % 3) * 12);

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <header className="border-b border-neutral-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center justify-between">
          <Link
            href="/"
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-bold"
          >
            <Music className="w-6 h-6" />
            <span>Auto-Playlist</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link href="/login">
              <AppButton variant="ghost" size="sm">로그인</AppButton>
            </Link>
            <Link href="/register">
              <AppButton size="sm">무료로 시작</AppButton>
            </Link>
          </div>
        </div>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-medium">
              <Sparkles className="w-3.5 h-3.5" />
              AI 음악 + 영상 자동화
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 leading-tight">
              AI가 음악도, 영상도 —{' '}
              <span className="text-purple-600 whitespace-nowrap">YouTube 채널</span>
              <br className="hidden sm:block" /> 자동으로 운영하세요
            </h1>

            <p className="text-base sm:text-lg text-neutral-600 leading-relaxed">
              참고곡만 골라주면 음악 생성부터 영상 제작·업로드까지 알아서.
              채널 운영에 들이던 시간을 돌려드려요.
            </p>

            <div className="flex flex-col sm:flex-row gap-3">
              <Link href="/register" className="w-full sm:w-auto">
                <AppButton size="lg" className="w-full sm:w-auto min-h-[48px]">
                  무료로 시작하기
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </AppButton>
              </Link>
              <Link href="/login" className="w-full sm:w-auto">
                <AppButton size="lg" variant="outline" className="w-full sm:w-auto min-h-[48px]">
                  로그인
                </AppButton>
              </Link>
            </div>

            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-neutral-500">
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-green-500" /> 가입 즉시 50크레딧
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-green-500" /> 신용카드 불필요
              </span>
              <span className="flex items-center gap-1.5">
                <Check className="w-4 h-4 text-green-500" /> 간편 가입
              </span>
            </div>
          </div>

          {/* 라이브 데모 — VideoLivePreview 자동 순환 */}
          <div className="relative">
            <div
              className="absolute -inset-4 bg-gradient-to-br from-purple-300 to-blue-300 rounded-3xl blur-3xl opacity-40"
              aria-hidden
            />
            <div className="relative bg-white rounded-2xl shadow-xl p-4 sm:p-6 border border-neutral-200">
              <p className="text-xs font-medium text-neutral-500 mb-3 flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5 text-purple-500" />
                실시간 데모 — 옵션이 자동으로 바뀝니다
              </p>
              <VideoLivePreview
                aspect="16:9"
                color={color}
                posY={posY}
                spectrumWidth={120}
                visualizerType={visualizerType}
                trackInfoStyle="player"
                trackTitle={trackTitle}
              />
              <p className="text-xs text-neutral-400 mt-3 text-center">
                실제 영상은 음악·렌더 결과에 따라 다르게 출력돼요
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────────── */}
      <section className="bg-white border-y border-neutral-100">
        <div className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">
              세 단계로 끝나는 채널 운영
            </h2>
            <p className="text-neutral-600">참고곡만 고르면 나머지는 알아서</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              {
                num: '01',
                icon: <Sparkles className="w-6 h-6" />,
                title: 'AI 음악 자동 생성',
                desc: '참고곡 5~8개만 고르면 비슷한 분위기의 트랙을 AI가 만들어요. 사용자는 마음에 드는 버전만 고르면 끝.',
              },
              {
                num: '02',
                icon: <Wand2 className="w-6 h-6" />,
                title: '영상 자동 제작',
                desc: '시각화·트랙 정보·인트로/아웃트로까지 자동 합성. 짧은 영상(쇼츠)부터 1시간 BGM까지 자유롭게.',
              },
              {
                num: '03',
                icon: <Upload className="w-6 h-6" />,
                title: 'YouTube 원클릭 업로드',
                desc: '채널 연결은 한 번이면 끝. 다음부터는 클릭 한 번으로 업로드, 예약 공개도 가능해요.',
              },
            ].map(({ num, icon, title, desc }) => (
              <AppCard key={num} className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center">
                    {icon}
                  </div>
                  <span className="text-xs font-mono text-neutral-400">{num}</span>
                </div>
                <h3 className="text-lg font-semibold text-neutral-900 mb-2">{title}</h3>
                <p className="text-sm text-neutral-600 leading-relaxed">{desc}</p>
              </AppCard>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <section className="max-w-6xl mx-auto px-4 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900 mb-3">
            채널 운영을 가장 쉽게
          </h2>
          <p className="text-neutral-600">
            크리에이터가 정말로 필요한 것만 깔끔하게
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
          {[
            {
              icon: <Eye className="w-5 h-5" />,
              title: '실시간 미리보기',
              desc: '색상·시각화 스타일·트랙 정보 표시 옵션을 바꿀 때마다 즉시 결과 확인. 만들기 전에 바로 보여요.',
            },
            {
              icon: <Wand2 className="w-5 h-5" />,
              title: '짧고 긴 영상 모두',
              desc: '9:16 쇼츠부터 16:9 1시간 BGM까지. YouTube Live 24/7 스트리밍 프로파일도 지원.',
            },
            {
              icon: <Laptop className="w-5 h-5" />,
              title: '내 컴퓨터에서 빠르게',
              desc: 'macOS 앱이 ffmpeg 인코딩과 YouTube 업로드를 로컬에서 처리. 서버 큐 대기 없음.',
            },
          ].map(({ icon, title, desc }) => (
            <AppCard key={title} className="p-6">
              <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center mb-4">
                {icon}
              </div>
              <h3 className="text-base font-semibold text-neutral-900 mb-2">{title}</h3>
              <p className="text-sm text-neutral-600 leading-relaxed">{desc}</p>
            </AppCard>
          ))}
        </div>
      </section>

      {/* ── 사회적 증거 (실제 운영 채널 사례) ─────────────────────────── */}
      <SocialProofSection />

      {/* ── Bottom CTA ───────────────────────────────────────────────── */}
      <section className="bg-gradient-to-br from-purple-600 to-blue-600 text-white">
        <div className="max-w-4xl mx-auto px-4 py-12 sm:py-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">
            지금 바로 시작해보세요
          </h2>
          <p className="text-purple-100 mb-6 sm:mb-8 text-base sm:text-lg">
            참고곡만 있으면 다음 영상은 5분 안에 만들 수 있어요.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/register" className="w-full sm:w-auto">
              <AppButton
                size="lg"
                className="w-full sm:w-auto min-h-[48px] bg-white text-purple-600 hover:bg-purple-50"
              >
                무료로 시작하기
                <ArrowRight className="w-4 h-4 ml-1.5" />
              </AppButton>
            </Link>
            <Link href="/login" className="w-full sm:w-auto">
              <AppButton
                size="lg"
                variant="ghost"
                className="w-full sm:w-auto min-h-[48px] text-white hover:bg-white/10"
              >
                로그인
              </AppButton>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <Footer />
    </div>
  );
}
